import AbsCaptcha from "./AbsCaptcha";
import type { CaptchaType } from "./Captcha";
import type { CaptchaData } from './AbsCaptcha'
/**
 * recaptcha의 경우 checkbox 는 사용하지 않는게 현재의 내부 방침이다.
 *
 * 즉, 아래의 코드는 v2, invisible 타입 고정이다.
 * 구글 뱃지를 hidden 시키고 싶으면 https://wiki.ncsoft.com/display/PTG/captcha 참조
 */

/* 커스텀 이벤트 사용예시
    // 이벤트 생성
    const cutomeEventCaptchaOnload_temp = new CustomEvent<CaptchaOnLoadDetail>(CAPTCHA_ONLOAD, {
    detail: true,
    bubbles: true,
    });

    // 이벤트 리스닝
    document.addEventListener(CAPTCHA_ONLOAD, (event: Event) => {
    const customEvent = event as CustomEvent<CaptchaOnLoadDetail>;
    console.log(customEvent.detail); // true
    });

    // 이벤트 디스패치
    document.dispatchEvent(cutomeEventCaptchaOnload_temp);
*/

/**
 * 커스텀 이벤트 - DOM 의 커스텀 이벤트 구현으로 DOM 의 커스텀 이벤트를 사용 하였음.
 */
type CaptchaExcuteDetail = string;
const CAPTCHA_ONEXCUTED = 'captcha:excute';

type CaptchaOnLoadDetail = boolean;
const CAPTCHA_ONLOAD = 'captcha:onload';

const BADGE_POSION_LIST = ['bottomright', 'bottomleft', 'inline']
export type BadgePosition = typeof BADGE_POSION_LIST[number];
const DEFAULT_BADGE_POSITION = 'bottomright';
export default class GoogleCaptcha extends AbsCaptcha {
  #errorCount = 0;
  protected token?: string;

  CAPTCHA_ONEXCUTED_LISTENER?: (vent: Event) => void

  constructor(protected ElWrapperId: string, protected captchaType: CaptchaType, protected clientKey: string, protected badgePosition: BadgePosition = DEFAULT_BADGE_POSITION) {
    super();
  }

  #setBadgePosition() {
    if (!BADGE_POSION_LIST.includes(this.badgePosition)) {
      console.error('뱃지 포지션 값이 잘못 되었습니다.')
      this.badgePosition = DEFAULT_BADGE_POSITION
    }
  }

  init() {
    return new Promise<AbsCaptcha>((res) => {
      const GRECAPTCHA_ONLOAD = 'grecaptchaOnload';

      const ElScript = document.createElement('script');
      ElScript.type = 'text/javascript';
      ElScript.src = `https://www.google.com/recaptcha/api.js?render=explicit&onload=${GRECAPTCHA_ONLOAD}`;
      // ElScript.onload = obj._render;
      window[GRECAPTCHA_ONLOAD] = this.#render;
      document.body.appendChild(ElScript);

      // 뱃지 포지션 검증
      this.#setBadgePosition();

      // 커스텀 이벤트 리스너 추가 CAPTCHA_ONLOAD - .js 파일 불러오고(콜백)), grecaptcha가 렌더된후(window.grecaptcha.ready()) CAPTCHA_ONLOAD 이벤트 디스패치
      document.getElementById(this.ElWrapperId)!.addEventListener(CAPTCHA_ONLOAD, (event: Event) => {
        const customEvent = event as CustomEvent<CaptchaOnLoadDetail>;
        res(this); // boolean
      });
    });
  }

  refresh() {
    // 테스트 결과 reset 메서드는 동기 함수이다. 즉, reset(); 하고 바로 execute() 해도 괜찮다.
    window.grecaptcha.reset();
    // 커스텀 이벤트 리스너 삭제 CAPTCHA_ONEXCUTED
    this.CAPTCHA_ONEXCUTED_LISTENER && document.getElementById(this.ElWrapperId)!.removeEventListener(CAPTCHA_ONEXCUTED, this.CAPTCHA_ONEXCUTED_LISTENER);
    this.CAPTCHA_ONEXCUTED_LISTENER = undefined;
  }

  execute() {
    return new Promise((res, rej) => {
      // v2 에서는 execute를 연속으로 실행시 render시 설정해준 callback이 동작하지 않는다. - https://stackoverflow.com/questions/66061518/google-recaptcha-v2-invisible-only-fires-onces

      // 따라서 getResponse() 먼저 체크 함
      if (window.grecaptcha.getResponse()) {
        res(this.captchaData)
        return;
      };

      // 아직 excute 되지 않아서 response가 없다면
      window.grecaptcha.execute(); // 성공하면 render에서 설정해준 callback 실행,

      this.CAPTCHA_ONEXCUTED_LISTENER = this.#CAPTCHA_ONEXCUTED_LISTENER_FACTORY(res)
      // 커스텀 이벤트 리스너 추가 CAPTCHA_ONEXCUTED - window.grecaptcha.execute() 후 발생되는 콜백에서 CAPTCHA_ONEXCUTED_LISTENER 디스패치
      document.getElementById(this.ElWrapperId)!.addEventListener(CAPTCHA_ONEXCUTED, this.CAPTCHA_ONEXCUTED_LISTENER);
    });
  }

  #CAPTCHA_ONEXCUTED_LISTENER_FACTORY = (res: (value: CaptchaData) => void) => {
    return (event: Event) => {
      const customEvent = event as CustomEvent<CaptchaExcuteDetail>;

      res(this.captchaData);
    };
  }

  get captchaData(): CaptchaData {
    return {
      type: this.captchaType,
      response: window.grecaptcha.getResponse(),
      clientKey: this.clientKey,
    };
  }

  // https://www.google.com/recaptcha/api.js 로드 되고 난후, onload 콜백의 파라미터로 넘긴 메서드
  #render = () => {
    window.grecaptcha.render(this.ElWrapperId, {
      // sitekey: '6LcGddgaAAAAAEra4c4WUAe1M-TUoiJgGUHsrdPr',
      sitekey: this.clientKey,
      callback: this.#onCallback,
      size: 'invisible', // * invisible 타입 고정 *
      badge: this.badgePosition,
      // 'expired-callback': '', // 없어도 잘동작함
      'error-callback': this.#onErrorCallback,
    });

    // grecaptcha.ready 가 실제로 있긴 한데 문서에는 잘 안나와 았음.
    window.grecaptcha.ready(() => {
      // 커스텀 이벤트 디스패치 CAPTCHA_ONLOAD
      document.getElementById(this.ElWrapperId)!.dispatchEvent(new CustomEvent<CaptchaOnLoadDetail>(CAPTCHA_ONLOAD, { bubbles: true, detail: true }));
    });
  }

  // reCapcha excute 되고 나서
  #onCallback = (token: string) => {
    // 커스텀 이벤트 디스패치 CAPTCHA_ONEXCUTED
    document.getElementById(this.ElWrapperId)!.dispatchEvent(new CustomEvent<CaptchaExcuteDetail>(CAPTCHA_ONEXCUTED, { bubbles: true, detail: token }));
  }

  #onErrorCallback() {
    // 캡챠 로딩이 연속적으로 실패 할 경우
    if (this.#errorCount!++ > 5) {
      this.execute = () => Promise.resolve(''); // 캡챠가 안붙으면 execute시 '' 생성 (프론트 애러 방지?)
      console.error('캡챠 로딩 실패');
    } else {
      setTimeout(window.grecaptcha.reset, 500);
    }
  }
}


